// React
import { useCallback } from 'react'

// Models
import { useStores } from 'models'

// Axios
import { AxiosRequestConfig } from 'axios'

// Interfaces
import { IAuthAuthenticatedUser, IAuthOidc } from 'interfaces/auth'

// Constant
import {
  LOCAL_STORAGE_ACCESS_TOKEN,
  LOCAL_STORAGE_AUTHENTICATED_USER,
  LOCAL_STORAGE_REFRESH_TOKEN,
  LOCAL_STORAGE_IS_AUTHENTICATED,
} from 'constant/local-storage.constant'

const useAuth = () => {
  // Hook
  const {
    auth: {
      auth_isLoading,
      auth_isVerifyLoading,
      auth_isAuthenticated,
      auth_authenticatedUser,
      auth_accessToken,
      auth_refreshToken,
      auth_INITIALIZE,
      auth_SET_AUTHENTICATED,
      auth_getAuthenticatedUser: authGetAuthenticatedUser,
      auth_changeRole: authChangeRole,
      auth_logout: authLogout,
      auth_setToken: authSetToken,
    },
  } = useStores()
  const auth_authenticatedUserRoles = auth_authenticatedUser.roles ? auth_authenticatedUser.roles : []
  const auth_persisted: {
    auth_accessToken?: string
    auth_refreshToken?: string
    auth_authenticatedUser?: IAuthAuthenticatedUser
    auth_isAuthenticated?: boolean
  } = {
    auth_accessToken: auth_accessToken || localStorage.getItem(LOCAL_STORAGE_ACCESS_TOKEN) || '',
    auth_refreshToken: auth_refreshToken || localStorage.getItem(LOCAL_STORAGE_REFRESH_TOKEN) || '',
    auth_authenticatedUser: auth_authenticatedUser.id
      ? JSON.parse(localStorage.getItem(LOCAL_STORAGE_AUTHENTICATED_USER) || '{}')
      : auth_authenticatedUser,
    auth_isAuthenticated: auth_isAuthenticated
      ? auth_isAuthenticated
      : Boolean(localStorage.getItem(LOCAL_STORAGE_IS_AUTHENTICATED)),
  }

  /**
   * @description Set token
   *
   * @param {IAuthOidc} payload
   *
   * @return {void} void
   */
  const auth_setToken = useCallback(
    (payload: IAuthOidc): void => {
      authSetToken(payload)
    },
    [authSetToken],
  )

  /**
   * @description Get authenticated user
   *
   * @return {Promise<void>} Promise<void>
   */
  const auth_getAuthenticatedUser = useCallback((): Promise<void> => {
    return new Promise((resolve, reject) => {
      authGetAuthenticatedUser({
        onSuccess: resolve,
        onError: reject,
      })
    })
  }, [authGetAuthenticatedUser])

  /**
   * @description Change authenticated user role
   *
   * @param {number} roleId
   * @param {AxiosRequestConfig} requestConfig
   *
   * @return {Promise<void>} Promise<void>
   */
  const auth_changeRole = useCallback(
    (roleId: number, requestConfig?: AxiosRequestConfig): Promise<void> => {
      return new Promise((resolve, reject) => {
        authChangeRole({
          body: { roleId },
          requestConfig: { ...(requestConfig || {}) },
          onSuccess: resolve,
          onError: reject,
        })
      })
    },
    [authChangeRole],
  )

  /**
   * @description Logout an user
   *
   * @return {void} void
   */
  const auth_logout = useCallback((): void => {
    authLogout()
  }, [authLogout])

  return {
    auth_isLoading,
    auth_persisted,
    auth_isVerifyLoading,
    auth_isAuthenticated,
    auth_authenticatedUser,
    auth_accessToken,
    auth_refreshToken,
    auth_authenticatedUserRoles,
    auth_INITIALIZE,
    auth_SET_AUTHENTICATED,
    auth_setToken,
    auth_getAuthenticatedUser,
    auth_changeRole,
    auth_logout,
  }
}

export { useAuth }
